import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { standardsLabel } from "../utils/contractParties";
import { getSignificanceThreshold } from "../constants/riskAnalysisMatrix";
import { CollapsiblePane, CollapsiblePaneGroup } from "./CollapsiblePane";

const ISSUE_CATEGORIES = ["Internal Issues", "External Issues", "Risk and Threats", "Opportunities for Improvement"];

// Backlog #19 — Gap Analysis Questionnaire (Advisor persona): the document's body sections
// below are wrapped in the shared CollapsiblePane primitive so they collapse/expand
// individually, plus a Collapse All / Expand All toggle via CollapsiblePaneGroup. This is
// UI-only chrome around content that was already conditionally rendered by isQModalEditMode /
// questionnaireFinalized / step3Status — none of that gating is touched here.
const GAP_QUESTIONNAIRE_PAGE_ID = "gap-analysis-questionnaire-doc";

export function GapQuestionnaireContractsPanel({
  project,
  qModalSections,
  setQModalSections,
  isQModalEditMode,
  setIsQModalEditMode,
  questionnaireFinalized,
  questionnaireSaveBusy,
  saveQuestionnaire,
  step3Status,
  isLeadAdvisor,
  isAdvisor,
  openOnboardingReviewModal,
  riskScale,
  domains,
  selectedQuestions,
  selectedIssueRows,
}) {
  return /* @__PURE__ */ jsxs("div", {
    className: "bg-[var(--color-bg-white)] rounded-[2rem] border border-[color:var(--color-border)] shadow-2xl overflow-hidden",
    children: [
      /* @__PURE__ */ jsxs("div", {
        className: "bg-[var(--color-surface-sunken)] border-b border-[color:var(--color-border)] px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
        children: [
          /* @__PURE__ */ jsxs("div", {
            children: [
              /* @__PURE__ */ jsx("h3", {
                className: "text-[color:var(--color-text-hero)] font-black text-lg tracking-tight",
                children: "Gap Analysis Questionnaire",
              }),
              /* @__PURE__ */ jsxs("p", {
                className: "text-[color:var(--color-accent)] text-[10px] font-bold uppercase tracking-widest mt-0.5",
                children: [standardsLabel(project), standardsLabel(project) ? " - " : "", qModalSections.companyName],
              }),
            ],
          }),
          /* @__PURE__ */ jsxs("div", {
            className: "flex flex-wrap items-center gap-2",
            children: [
              !questionnaireFinalized &&
                /* @__PURE__ */ jsx("button", {
                  onClick: () => setIsQModalEditMode(!isQModalEditMode),
                  className: "px-3 py-2 text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)] transition-colors text-xs font-bold uppercase",
                  children: isQModalEditMode ? "Preview" : "Edit",
                }),
              !questionnaireFinalized &&
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  disabled: questionnaireSaveBusy,
                  onClick: () => saveQuestionnaire(),
                  className:
                    "px-4 py-2 bg-[var(--color-accent-fill)] hover:brightness-110 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                  children: questionnaireSaveBusy ? "Saving\u2026" : "Finalize questionnaire",
                }),
              (isLeadAdvisor || isAdvisor) &&
                questionnaireFinalized &&
                step3Status === "Draft" &&
                /* @__PURE__ */ jsx("button", {
                  type: "button",
                  onClick: () => openOnboardingReviewModal("Under Review"),
                  className:
                    "px-4 py-2 bg-[var(--color-bg-white)] text-[color:var(--color-text-hero)] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[var(--color-info-bg)] transition-all shadow-lg",
                  children: "Send for Review",
                }),
            ],
          }),
        ],
      }),
      /* @__PURE__ */ jsxs("div", {
        className: "px-6 sm:px-10 py-8 space-y-8 max-h-[min(75vh,900px)] overflow-y-auto paper-surface bg-white text-slate-900",
        style: { fontFamily: "'Times New Roman', Georgia, serif" },
        children: [
          /* @__PURE__ */ jsxs("div", {
            className: "flex items-center justify-between border-b-2 border-slate-200 pb-6",
            children: [
              /* @__PURE__ */ jsxs("div", {
                children: [
                  /* @__PURE__ */ jsx("div", {
                    className:
                      "w-20 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-[8px] font-bold text-slate-400 uppercase",
                    children: "Company Logo",
                  }),
                  isQModalEditMode
                    ? /* @__PURE__ */ jsx("input", {
                        value: qModalSections.companyName,
                        onChange: (e) => setQModalSections((p) => ({ ...p, companyName: e.target.value })),
                        className:
                          "mt-1 text-sm font-bold text-slate-800 bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none w-full",
                      })
                    : /* @__PURE__ */ jsx("p", {
                        className: "mt-1 text-sm font-bold text-slate-800",
                        children: qModalSections.companyName,
                      }),
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                className: "text-right",
                children: [
                  /* @__PURE__ */ jsx("div", {
                    className:
                      "w-20 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-[8px] font-bold text-slate-400 uppercase ml-auto",
                    children: "Advisor Logo",
                  }),
                  isQModalEditMode
                    ? /* @__PURE__ */ jsx("input", {
                        value: qModalSections.advisorName,
                        onChange: (e) => setQModalSections((p) => ({ ...p, advisorName: e.target.value })),
                        className:
                          "mt-1 text-sm font-bold text-slate-800 bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none w-full text-right",
                      })
                    : /* @__PURE__ */ jsx("p", {
                        className: "mt-1 text-sm font-bold text-slate-800",
                        children: qModalSections.advisorName,
                      }),
                ],
              }),
            ],
          }),
          /* @__PURE__ */ jsx("h2", {
            className: "text-center text-xl font-black tracking-wide text-slate-900",
            children: "Gap Analysis Questionnaire",
          }),
          /* @__PURE__ */ jsx(CollapsiblePaneGroup, {
            pageId: GAP_QUESTIONNAIRE_PAGE_ID,
            children: [
              /* @__PURE__ */ jsx(
                CollapsiblePane,
                {
                  id: "scope",
                  pageId: GAP_QUESTIONNAIRE_PAGE_ID,
                  title: "Scope",
                  children: /* @__PURE__ */ jsxs("div", {
                    className: "grid grid-cols-[180px_1fr] gap-y-3 gap-x-4 text-sm",
                    children: [
                      /* @__PURE__ */ jsx("span", { className: "text-slate-500 font-semibold", children: "Legal Entities + Locations" }),
                      isQModalEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            value: qModalSections.legalEntities,
                            onChange: (e) => setQModalSections((p) => ({ ...p, legalEntities: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none text-sm text-slate-700",
                          })
                        : /* @__PURE__ */ jsx("span", { className: "text-slate-700", children: qModalSections.legalEntities }),
                      /* @__PURE__ */ jsx("span", { className: "text-slate-500 font-semibold", children: "Products and Services" }),
                      isQModalEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            value: qModalSections.productsServices,
                            onChange: (e) => setQModalSections((p) => ({ ...p, productsServices: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none text-sm text-slate-700",
                          })
                        : /* @__PURE__ */ jsx("span", { className: "text-slate-700", children: qModalSections.productsServices }),
                      /* @__PURE__ */ jsx("span", { className: "text-slate-500 font-semibold", children: "Activities / Departments" }),
                      isQModalEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            value: qModalSections.activitiesDepts,
                            onChange: (e) => setQModalSections((p) => ({ ...p, activitiesDepts: e.target.value })),
                            className: "bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none text-sm text-slate-700",
                          })
                        : /* @__PURE__ */ jsx("span", { className: "text-slate-700", children: qModalSections.activitiesDepts }),
                    ],
                  }),
                },
                "scope",
              ),
              domains.map((domain) => {
            const dqs = selectedQuestions.filter((q) => q.selected && q.domain === domain);
            if (!dqs.length) return null;
            return /* @__PURE__ */ jsx(
              CollapsiblePane,
              {
                id: `domain-${domain}`,
                pageId: GAP_QUESTIONNAIRE_PAGE_ID,
                title: domain,
                summary: `${dqs.length} questions`,
                children: /* @__PURE__ */ jsxs(Fragment, {
                  children: [
                  /* @__PURE__ */ jsxs("table", {
                    className: "w-full text-sm",
                    children: [
                      /* @__PURE__ */ jsx("thead", {
                        children: /* @__PURE__ */ jsxs("tr", {
                          className: "text-left text-[9px] font-black uppercase text-slate-400 tracking-widest border-b",
                          children: [
                            /* @__PURE__ */ jsx("th", { className: "py-2 w-10", children: "#" }),
                            /* @__PURE__ */ jsx("th", { className: "py-2", children: "Question" }),
                            /* @__PURE__ */ jsx("th", { className: "py-2 w-24 text-right", children: "Response" }),
                          ],
                        }),
                      }),
                      /* @__PURE__ */ jsx("tbody", {
                        children: dqs.map((q, i) =>
                          /* @__PURE__ */ jsxs(
                            "tr",
                            {
                              className: "border-b border-slate-50",
                              children: [
                                /* @__PURE__ */ jsx("td", { className: "py-2 text-slate-400 font-bold", children: i + 1 }),
                                /* @__PURE__ */ jsx("td", { className: "py-2 text-slate-700", children: q.question }),
                                /* @__PURE__ */ jsx("td", {
                                  className: "py-2 text-right",
                                  children: /* @__PURE__ */ jsx("span", {
                                    className: "px-2 py-0.5 bg-slate-50 text-slate-400 rounded text-[9px] font-bold",
                                    children: q.responseType,
                                  }),
                                }),
                              ],
                            },
                            q.id,
                          ),
                        ),
                      }),
                    ],
                  }),
                  domain === "Organization" &&
                    /* @__PURE__ */ jsxs("div", {
                      className: "space-y-1 text-xs text-slate-500 mt-2 pl-2",
                      children: [
                        /* @__PURE__ */ jsx("p", { children: "\u2022 Attach latest copy of certificates" }),
                        /* @__PURE__ */ jsx("p", { children: "\u2022 Attach latest copy of any independent review" }),
                        /* @__PURE__ */ jsx("p", { children: "\u2022 Attach any copy of assessment done in last 18 months" }),
                        /* @__PURE__ */ jsx("p", { children: "\u2022 Attach any specific requirement stated by your stakeholder" }),
                      ],
                    }),
                  ],
                }),
              },
              domain,
            );
              }),
              /* @__PURE__ */ jsx(
                CollapsiblePane,
                {
                  id: "issues-threats-opportunities",
                  pageId: GAP_QUESTIONNAIRE_PAGE_ID,
                  title: "Issues - Threats - Opportunities",
                  children: /* @__PURE__ */ jsxs(Fragment, {
                    children: [
              /* @__PURE__ */ jsx("p", {
                className: "text-xs text-slate-500 pl-1",
                children: "Select top 10 most relevant from each category",
              }),
              /* @__PURE__ */ jsx("div", {
                className: "grid grid-cols-2 gap-4",
                children: ISSUE_CATEGORIES.map((cat) =>
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: "bg-slate-50 rounded-xl p-4 border border-slate-100",
                      children: [
                        /* @__PURE__ */ jsx("h5", {
                          className: "text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2",
                          children: cat,
                        }),
                        /* @__PURE__ */ jsx("ul", {
                          className: "space-y-1",
                          children: (selectedIssueRows[cat] || []).map((item, i) =>
                            /* @__PURE__ */ jsxs(
                              "li",
                              {
                                className: "text-xs text-slate-600 flex items-start gap-1.5",
                                children: [
                                  /* @__PURE__ */ jsx("span", { className: "text-slate-400 shrink-0", children: "\u2022" }),
                                  typeof item === "string" ? item : item?.issue,
                                ],
                              },
                              i,
                            ),
                          ),
                        }),
                      ],
                    },
                    cat,
                  ),
                ),
              }),
                    ],
                  }),
                },
                "issues-threats-opportunities",
              ),
              /* @__PURE__ */ jsx(
                CollapsiblePane,
                {
                  id: "other-info",
                  pageId: GAP_QUESTIONNAIRE_PAGE_ID,
                  title: "Any other relevant information",
                  children:
              isQModalEditMode
                ? /* @__PURE__ */ jsx("textarea", {
                    value: qModalSections.otherInfo,
                    onChange: (e) => setQModalSections((p) => ({ ...p, otherInfo: e.target.value })),
                    className: "w-full bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-slate-700 outline-none min-h-[80px]",
                  })
                : /* @__PURE__ */ jsx("p", {
                    className: "text-sm text-slate-700 px-4 py-3 bg-slate-50 rounded-xl border",
                    children: qModalSections.otherInfo || "-",
                  }),
                },
                "other-info",
              ),
              /* @__PURE__ */ jsx(
                CollapsiblePane,
                {
                  id: "affirmation",
                  pageId: GAP_QUESTIONNAIRE_PAGE_ID,
                  title: "Affirmation Statement",
                  children: /* @__PURE__ */ jsxs(Fragment, {
                    children: [
              /* @__PURE__ */ jsxs("p", {
                className: "text-sm text-slate-700 leading-relaxed",
                children: [
                  "I, affirm that the information provided in this questionnaire is true, accurate, and complete to the best of my knowledge and belief. I also hereby provide the consent on behalf of ",
                  /* @__PURE__ */ jsx("strong", { children: qModalSections.companyName }),
                  " to ",
                  /* @__PURE__ */ jsx("strong", { children: qModalSections.advisorName }),
                  " to conduct technical assessment of the technical infrastructure as per the requirements agreed in the Scope of Work.",
                ],
              }),
              /* @__PURE__ */ jsxs("div", {
                className: "grid grid-cols-2 gap-4 mt-4",
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    children: [
                      /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 font-bold", children: "Name" }),
                      isQModalEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            value: qModalSections.affirmationName,
                            onChange: (e) => setQModalSections((p) => ({ ...p, affirmationName: e.target.value })),
                            className: "w-full mt-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none text-sm text-slate-700",
                          })
                        : /* @__PURE__ */ jsx("p", {
                            className: "text-sm font-semibold text-slate-800 mt-1",
                            children: qModalSections.affirmationName,
                          }),
                    ],
                  }),
                  /* @__PURE__ */ jsxs("div", {
                    children: [
                      /* @__PURE__ */ jsx("span", { className: "text-xs text-slate-400 font-bold", children: "Date" }),
                      isQModalEditMode
                        ? /* @__PURE__ */ jsx("input", {
                            type: "date",
                            value: qModalSections.affirmationDate,
                            onChange: (e) => setQModalSections((p) => ({ ...p, affirmationDate: e.target.value })),
                            className: "w-full mt-1 bg-blue-50 border border-blue-200 rounded px-2 py-1 outline-none text-sm text-slate-700",
                          })
                        : /* @__PURE__ */ jsx("p", {
                            className: "text-sm font-semibold text-slate-800 mt-1",
                            children: qModalSections.affirmationDate,
                          }),
                    ],
                  }),
                ],
              }),
                    ],
                  }),
                },
                "affirmation",
              ),
            ],
          }),
          /* @__PURE__ */ jsx("div", {
            className: "space-y-2 border-t border-slate-200 pt-4",
            children: /* @__PURE__ */ jsxs("div", {
              className: "flex items-center gap-3 text-xs text-slate-500",
              children: [
                /* @__PURE__ */ jsx("span", { className: "font-bold", children: "Risk Scale:" }),
                /* @__PURE__ */ jsxs("span", {
                  className: "px-2 py-1 bg-blue-50 text-blue-700 rounded font-black",
                  children: [riskScale, "-Point"],
                }),
                /* @__PURE__ */ jsx("span", { className: "font-bold ml-4", children: "Significance Threshold:" }),
                /* @__PURE__ */ jsx("span", {
                  className: "px-2 py-1 bg-amber-50 text-amber-700 rounded font-black",
                  children: String(getSignificanceThreshold(riskScale)),
                }),
              ],
            }),
          }),
          /* @__PURE__ */ jsx("p", {
            className: "text-[9px] text-slate-400 text-center italic pt-2",
            children: "Confidential",
          }),
        ],
      }),
    ],
  });
}
